import React, { useEffect, useState } from 'react';
import { materialService } from '../../services';
import {
  Package, Search, Plus, ChevronDown, ChevronRight,
  Building2, TrendingUp, Star, Check, X, Edit2, Activity,
  AlertTriangle, Download
} from 'lucide-react';

// ── Constants ─────────────────────────────────────────────────
const TIER_COLORS: Record<string, string> = {
  ECONOMY:  'bg-gray-100 text-gray-700',
  STANDARD: 'bg-blue-100 text-blue-700',
  PREMIUM:  'bg-amber-100 text-amber-700',
  LUXURY:   'bg-purple-100 text-purple-700',
};
const STATUS_COLORS: Record<string, string> = {
  APPROVED:  'bg-green-100 text-green-700',
  PENDING:   'bg-amber-100 text-amber-700',
  REJECTED:  'bg-red-100 text-red-700',
  SUSPENDED: 'bg-gray-100 text-gray-500',
};
const CATEGORIES = ['Cement','Steel','Aggregate','Sand','Masonry','Waterproofing',
  'Plumbing','Electrical','Flooring','Paint','Timber','Hardware','Adhesive','Insulation','Other'];
const UNITS = ['KG','BAG','NOS','SFT','CFT','RFT','LTR','SQM','CUM','MT','BRASS','THOU','SET'];
const BLDG_TYPES = ['ALL','IH','VI','SB','MB','HR','BP','TW'];
const BLDG_LABELS: Record<string,string> = {
  ALL:'All Types', IH:'Independent House', VI:'Villa', SB:'Single Building',
  MB:'Multi-storey', HR:'High-rise', BP:'Builder Plot', TW:'Townhouse'
};

const inp  = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";
const inpRO = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-400 cursor-not-allowed";
const fmtDate = (d?: string) => d ? new Date(d).toLocaleDateString('en-IN') : '—';

// ── Star Rating Component ─────────────────────────────────────
const StarRating = ({ value, onChange, readonly = false }: {
  value: number; onChange?: (v: number) => void; readonly?: boolean;
}) => (
  <div className="flex gap-0.5">
    {[1,2,3,4,5].map(s => (
      <button key={s} type="button"
        onClick={() => !readonly && onChange && onChange(s)}
        className={`${readonly ? 'cursor-default' : 'cursor-pointer hover:scale-110'} transition-transform`}>
        <Star size={14} className={s <= value ? 'fill-amber-400 text-amber-400' : 'text-gray-300'} />
      </button>
    ))}
  </div>
);

// ── Export CSV helper ─────────────────────────────────────────
const exportCSV = (data: any[], filename: string) => {
  if (!data.length) return;
  const keys = Object.keys(data[0]);
  const csv = [keys.join(','), ...data.map(row =>
    keys.map(k => `"${String(row[k] ?? '').replace(/"/g, '""')}"`).join(',')
  )].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename;
  a.click(); URL.revokeObjectURL(url);
};

// ── Price Alert Badge ─────────────────────────────────────────
const PriceAlertBadge = ({ pct }: { pct: number }) => {
  if (pct <= 15) return null;
  return (
    <span className="flex items-center gap-1 text-xs text-red-600 bg-red-50 px-1.5 py-0.5 rounded-full">
      <AlertTriangle size={10} /> +{pct.toFixed(0)}%
    </span>
  );
};

// ─────────────────────────────────────────────────────────────
// ── Add/Edit Material Modal ───────────────────────────────────
// ─────────────────────────────────────────────────────────────
const MaterialModal = ({ material, onClose, onSaved }: {
  material?: any; onClose: () => void; onSaved: () => void;
}) => {
  const isEdit = !!material;
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const [form, setForm]     = useState({
    mat_code:        material?.mat_code        || '',
    mat_name:        material?.mat_name        || '',
    category:        material?.category        || 'Cement',
    sub_category:    material?.sub_category    || '',
    unit_of_measure: material?.unit_of_measure || 'KG',
    secondary_unit:  material?.secondary_unit  || '',
    wastage_pct:     material?.wastage_pct     || 5,
    min_order_qty:   material?.min_order_qty   || 1,
    storage_notes:   material?.storage_notes   || '',
    substitutable:   material?.substitutable   || false,
    is_structural:   material?.is_structural   || false,
    is_standard:     material?.is_standard     || '',
    is_active:       material?.is_active !== undefined ? material.is_active : true,
  });
  const set = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      const { default: api } = await import('../../utils/api');
      if (isEdit) {
        await api.put(`/material/materials/${material.mat_id}`, form);
      } else {
        await api.post('/material/materials', form);
      }
      onSaved();
    } catch (err: any) { setError(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
          <h3 className="font-bold text-gray-900">{isEdit ? 'Edit Material' : 'Add Material'}</h3>
          <button onClick={onClose}><X size={18} className="text-gray-400" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-3">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-sm">{error}</div>}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">
                Code {!isEdit && <span className="text-gray-400 font-normal">(auto-generated)</span>}
              </label>
              <input value={isEdit ? form.mat_code : 'Auto-generated on save'}
                onChange={e => isEdit && set('mat_code', e.target.value)}
                readOnly={!isEdit} className={isEdit ? inp : inpRO} />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Name *</label>
              <input required value={form.mat_name} onChange={e => set('mat_name', e.target.value)}
                placeholder="OPC 53 Grade Cement" className={inp} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Category *</label>
              <select value={form.category} onChange={e => set('category', e.target.value)} className={inp}>
                {CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Sub Category</label>
              <input value={form.sub_category} onChange={e => set('sub_category', e.target.value)}
                placeholder="OPC, PPC, TMT Bar..." className={inp} />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Unit *</label>
              <select value={form.unit_of_measure} onChange={e => set('unit_of_measure', e.target.value)} className={inp}>
                {UNITS.map(u => <option key={u}>{u}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Secondary Unit</label>
              <select value={form.secondary_unit} onChange={e => set('secondary_unit', e.target.value)} className={inp}>
                <option value="">None</option>
                {UNITS.map(u => <option key={u}>{u}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Wastage %</label>
              <input type="number" min={0} max={30} step={0.5} value={form.wastage_pct}
                onChange={e => set('wastage_pct', Number(e.target.value))} className={inp} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Min Order Qty</label>
              <input type="number" min={1} value={form.min_order_qty}
                onChange={e => set('min_order_qty', Number(e.target.value))} className={inp} />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">IS Standard</label>
              <input value={form.is_standard} onChange={e => set('is_standard', e.target.value)}
                placeholder="IS:12269, IS:2062..." className={inp} />
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-600 block mb-1">Storage Notes</label>
            <input value={form.storage_notes} onChange={e => set('storage_notes', e.target.value)}
              placeholder="Store in dry place, away from moisture..." className={inp} />
          </div>

          <div className="flex items-center gap-5 pt-1">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.is_structural}
                onChange={e => set('is_structural', e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-blue-600" />
              <span className="text-sm text-gray-700">Structural material</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.substitutable}
                onChange={e => set('substitutable', e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-blue-600" />
              <span className="text-sm text-gray-700">Can substitute</span>
            </label>
            {isEdit && (
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.is_active}
                  onChange={e => set('is_active', e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600" />
                <span className="text-sm text-gray-700">Active</span>
              </label>
            )}
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-200 text-gray-600 text-sm rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving}
              className="flex-1 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Saving...' : (isEdit ? 'Update Material' : 'Add Material')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// ── Add/Edit Variant Modal ────────────────────────────────────
// ─────────────────────────────────────────────────────────────
const VariantModal = ({ matId, matName, variant, onClose, onSaved }: {
  matId?: number; matName?: string; variant?: any; onClose: () => void; onSaved: () => void;
}) => {
  const isEdit = !!variant;
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const [form, setForm]     = useState({
    variant_code:    variant?.variant_code   || '',
    brand_name:      variant?.brand_name     || '',
    variant_name:    variant?.variant_name   || '',
    quality_tier:    variant?.quality_tier   || 'STANDARD',
    applicable_bldg: variant?.applicable_bldg|| 'ALL',
    gst_rate_pct:    variant?.gst_rate_pct   || 18,
    hsn_code:        variant?.hsn_code       || '',
    specification:   variant?.specification  || '',
    is_active:       variant?.is_active !== undefined ? variant.is_active : true,
  });
  const set = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      const { default: api } = await import('../../utils/api');
      if (isEdit) {
        await api.put(`/material/variants/${variant.variant_id}`, form);
      } else {
        await api.post('/material/variants', { ...form, mat_id: matId });
      }
      onSaved();
    } catch (err: any) { setError(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h3 className="font-bold text-gray-900">{isEdit ? 'Edit Variant' : 'Add Variant'}</h3>
            <p className="text-xs text-gray-400">{variant?.mat_name || matName}</p>
          </div>
          <button onClick={onClose}><X size={18} className="text-gray-400" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-3">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-sm">{error}</div>}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">
                Variant Code {!isEdit && <span className="text-gray-400 font-normal">(auto)</span>}
              </label>
              <input value={isEdit ? form.variant_code : 'Auto-generated'}
                onChange={e => isEdit && set('variant_code', e.target.value)}
                readOnly={!isEdit} className={isEdit ? inp : inpRO} />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Brand Name *</label>
              <input required value={form.brand_name} onChange={e => set('brand_name', e.target.value)}
                placeholder="Ultratech, JSW..." className={inp} />
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-600 block mb-1">Variant Name *</label>
            <input required value={form.variant_name} onChange={e => set('variant_name', e.target.value)}
              placeholder="Ultratech OPC 53 50kg" className={inp} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Quality Tier</label>
              <select value={form.quality_tier} onChange={e => set('quality_tier', e.target.value)} className={inp}>
                {['ECONOMY','STANDARD','PREMIUM','LUXURY'].map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Applicable Building</label>
              <select value={form.applicable_bldg} onChange={e => set('applicable_bldg', e.target.value)} className={inp}>
                {BLDG_TYPES.map(b => <option key={b} value={b}>{b} — {BLDG_LABELS[b]}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">GST %</label>
              <input type="number" value={form.gst_rate_pct}
                onChange={e => set('gst_rate_pct', Number(e.target.value))} className={inp} />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">HSN Code</label>
              <input value={form.hsn_code} onChange={e => set('hsn_code', e.target.value)}
                placeholder="2523" className={inp} />
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-600 block mb-1">Specification</label>
            <input value={form.specification} onChange={e => set('specification', e.target.value)}
              placeholder="IS:12269, Fe500D..." className={inp} />
          </div>

          {isEdit && (
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.is_active}
                onChange={e => set('is_active', e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-blue-600" />
              <span className="text-sm text-gray-700">Active</span>
            </label>
          )}

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-200 text-gray-600 text-sm rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving}
              className="flex-1 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Saving...' : (isEdit ? 'Update Variant' : 'Add Variant')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// ── Add/Edit Supplier Modal ───────────────────────────────────
// ─────────────────────────────────────────────────────────────
const SupplierModal = ({ supplier, onClose, onSaved }: {
  supplier?: any; onClose: () => void; onSaved: () => void;
}) => {
  const isEdit = !!supplier;
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const [tab, setTab]       = useState<'basic'|'bank'>('basic');
  const [form, setForm]     = useState({
    sup_code:             supplier?.sup_code             || '',
    company_name:         supplier?.company_name         || '',
    contact_person:       supplier?.contact_person       || '',
    mobile:               supplier?.mobile               || '',
    email:                supplier?.email                || '',
    gstin:                supplier?.gstin                || '',
    pan_number:           supplier?.pan_number           || '',
    address:              supplier?.address              || '',
    city:                 supplier?.city                 || 'Bengaluru',
    state:                supplier?.state                || 'Karnataka',
    payment_terms:        supplier?.payment_terms        || 'NET_30',
    lead_time_days:       supplier?.lead_time_days       || 3,
    credit_limit_amount:  supplier?.credit_limit_amount  || '',
    supply_areas:         supplier?.supply_areas         || '',
    rating:               supplier?.rating               || 0,
    bank_account:         supplier?.bank_account         || '',
    bank_ifsc:            supplier?.bank_ifsc            || '',
    bank_name:            supplier?.bank_name            || '',
    is_active:            supplier?.is_active !== undefined ? supplier.is_active : true,
  });
  const set = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      const { default: api } = await import('../../utils/api');
      if (isEdit) {
        await api.put(`/material/suppliers/${supplier.sup_id}`, form);
      } else {
        await api.post('/material/suppliers', form);
      }
      onSaved();
    } catch (err: any) { setError(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
          <h3 className="font-bold text-gray-900">{isEdit ? 'Edit Supplier' : 'Add Supplier'}</h3>
          <button onClick={onClose}><X size={18} className="text-gray-400" /></button>
        </div>

        {/* Sub-tabs */}
        <div className="flex gap-1 px-6 pt-4">
          {(['basic','bank'] as const).map(t => (
            <button key={t} type="button" onClick={() => setTab(t)}
              className={`px-4 py-1.5 text-xs font-medium rounded-lg transition-colors
                ${tab === t ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              {t === 'basic' ? 'Basic Info' : 'Bank Details'}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="p-6 space-y-3">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-sm">{error}</div>}

          {tab === 'basic' && <>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">
                  Supplier Code {!isEdit && <span className="text-gray-400 font-normal">(auto)</span>}
                </label>
                <input value={isEdit ? form.sup_code : 'Auto-generated'}
                  onChange={e => isEdit && set('sup_code', e.target.value)}
                  readOnly={!isEdit} className={isEdit ? inp : inpRO} />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Company Name *</label>
                <input required value={form.company_name} onChange={e => set('company_name', e.target.value)}
                  placeholder="ABC Building Materials" className={inp} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Contact Person *</label>
                <input required value={form.contact_person} onChange={e => set('contact_person', e.target.value)} className={inp} />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Mobile *</label>
                <input required value={form.mobile} onChange={e => set('mobile', e.target.value)} className={inp} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Email</label>
                <input type="email" value={form.email} onChange={e => set('email', e.target.value)} className={inp} />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">GSTIN *</label>
                <input required value={form.gstin} onChange={e => set('gstin', e.target.value)}
                  placeholder="29XXXXX1234F1Z5" className={inp} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">PAN Number</label>
                <input value={form.pan_number} onChange={e => set('pan_number', e.target.value)}
                  placeholder="ABCDE1234F" className={inp} />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Credit Limit (₹)</label>
                <input type="number" min={0} value={form.credit_limit_amount}
                  onChange={e => set('credit_limit_amount', e.target.value)}
                  placeholder="500000" className={inp} />
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Address *</label>
              <textarea required rows={2} value={form.address}
                onChange={e => set('address', e.target.value)} className={inp} />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">City *</label>
                <input required value={form.city} onChange={e => set('city', e.target.value)} className={inp} />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">State</label>
                <input value={form.state} onChange={e => set('state', e.target.value)} className={inp} />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Lead Time (days)</label>
                <input type="number" min={1} value={form.lead_time_days}
                  onChange={e => set('lead_time_days', Number(e.target.value))} className={inp} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Payment Terms</label>
                <select value={form.payment_terms} onChange={e => set('payment_terms', e.target.value)} className={inp}>
                  {['NET_15','NET_30','NET_45','ADVANCE','ON_DELIVERY','WEEKLY','FORTNIGHTLY'].map(t => (
                    <option key={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Supply Areas</label>
                <input value={form.supply_areas} onChange={e => set('supply_areas', e.target.value)}
                  placeholder="Whitefield, Sarjapur, HSR..." className={inp} />
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">
                Supplier Rating {isEdit && <span className="text-gray-400 font-normal">(click to set)</span>}
              </label>
              <StarRating value={form.rating} onChange={v => set('rating', v)} />
            </div>

            {isEdit && (
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.is_active}
                  onChange={e => set('is_active', e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600" />
                <span className="text-sm text-gray-700">Active Supplier</span>
              </label>
            )}
          </>}

          {tab === 'bank' && <>
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800 mb-2">
              Bank details are used for payment processing. Keep confidential.
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Bank Name</label>
              <input value={form.bank_name} onChange={e => set('bank_name', e.target.value)}
                placeholder="State Bank of India" className={inp} />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Account Number</label>
              <input value={form.bank_account} onChange={e => set('bank_account', e.target.value)}
                placeholder="XXXXXXXXXXXX" className={inp} />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">IFSC Code</label>
              <input value={form.bank_ifsc} onChange={e => set('bank_ifsc', e.target.value)}
                placeholder="SBIN0001234" className={inp} />
            </div>
          </>}

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-200 text-gray-600 text-sm rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving}
              className="flex-1 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Saving...' : (isEdit ? 'Update Supplier' : 'Add Supplier')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// ── Add/Edit Rate Modal ───────────────────────────────────────
// ─────────────────────────────────────────────────────────────
const RateModal = ({ rate, onClose, onSaved }: {
  rate?: any; onClose: () => void; onSaved: () => void;
}) => {
  const isEdit = !!rate;
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState('');
  const [materials, setMaterials]     = useState<any[]>([]);
  const [variants, setVariants]       = useState<any[]>([]);
  const [suppliers, setSuppliers]     = useState<any[]>([]);
  const [selMat, setSelMat]           = useState(rate?.mat_id?.toString() || '');
  const [form, setForm]               = useState({
    sup_id:             rate?.sup_id?.toString()      || '',
    variant_id:         rate?.variant_id?.toString()  || '',
    price_per_unit:     rate?.price_per_unit          || '',
    unit_of_measure:    rate?.unit_of_measure         || '',
    effective_from:     rate?.effective_from?.split('T')[0] || new Date().toISOString().split('T')[0],
    effective_to:       rate?.effective_to?.split('T')[0]   || '',
    includes_delivery:  rate?.includes_delivery !== undefined ? rate.includes_delivery : true,
    delivery_charges:   rate?.delivery_charges        || '',
    bulk_discount_pct:  rate?.bulk_discount_pct       || '',
  });
  const set = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    Promise.all([
      materialService.getAll(),
      materialService.getSuppliers({ is_approved: false })
    ]).then(([mRes, sRes]) => {
      setMaterials(mRes.data.data);
      setSuppliers(sRes.data.data);
    });
  }, []);

  useEffect(() => {
    if (selMat) {
      materialService.getVariants(Number(selMat)).then(r => setVariants(r.data.data));
    }
  }, [selMat]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      const { default: api } = await import('../../utils/api');
      if (isEdit) {
        await api.put(`/material/rates/${rate.rate_id}`, form);
      } else {
        await api.post('/material/rates', form);
      }
      onSaved();
    } catch (err: any) { setError(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
          <h3 className="font-bold text-gray-900">{isEdit ? 'Edit Rate' : 'Add Supplier Rate'}</h3>
          <button onClick={onClose}><X size={18} className="text-gray-400" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-3">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-sm">{error}</div>}

          <div>
            <label className="text-xs font-medium text-gray-600 block mb-1">Supplier *</label>
            <select required value={form.sup_id} onChange={e => set('sup_id', e.target.value)} className={inp}>
              <option value="">Select supplier...</option>
              {suppliers.map(s => <option key={s.sup_id} value={s.sup_id}>{s.company_name}</option>)}
            </select>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-600 block mb-1">Material *</label>
            <select required value={selMat} onChange={e => setSelMat(e.target.value)} className={inp}>
              <option value="">Select material...</option>
              {materials.map(m => <option key={m.mat_id} value={m.mat_id}>{m.mat_name}</option>)}
            </select>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-600 block mb-1">Variant / Brand *</label>
            <select required value={form.variant_id} onChange={e => set('variant_id', e.target.value)} className={inp}>
              <option value="">Select variant...</option>
              {variants.map(v => <option key={v.variant_id} value={v.variant_id}>{v.brand_name} — {v.variant_name}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Price per Unit (₹) *</label>
              <input required type="number" min={0} step={0.01} value={form.price_per_unit}
                onChange={e => set('price_per_unit', e.target.value)} className={inp} />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Unit *</label>
              <select required value={form.unit_of_measure} onChange={e => set('unit_of_measure', e.target.value)} className={inp}>
                <option value="">Select...</option>
                {UNITS.map(u => <option key={u}>{u}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Effective From *</label>
              <input required type="date" value={form.effective_from}
                onChange={e => set('effective_from', e.target.value)} className={inp} />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">
                Effective To <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <input type="date" value={form.effective_to}
                onChange={e => set('effective_to', e.target.value)} className={inp} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Bulk Discount %</label>
              <input type="number" min={0} max={30} step={0.5} value={form.bulk_discount_pct}
                onChange={e => set('bulk_discount_pct', e.target.value)}
                placeholder="0" className={inp} />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">
                Delivery Charges (₹)
                <span className="text-gray-400 font-normal ml-1">{form.includes_delivery ? '(included)' : ''}</span>
              </label>
              <input type="number" min={0} step={0.01} value={form.delivery_charges}
                onChange={e => set('delivery_charges', e.target.value)}
                disabled={form.includes_delivery}
                placeholder="0" className={form.includes_delivery ? inpRO : inp} />
            </div>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.includes_delivery}
              onChange={e => set('includes_delivery', e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-blue-600" />
            <span className="text-sm text-gray-700">Delivery charges included in price</span>
          </label>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-200 text-gray-600 text-sm rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving}
              className="flex-1 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Saving...' : (isEdit ? 'Update Rate' : 'Add Rate')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// ── Materials Tab ─────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────
const MaterialsTab = () => {
  const [materials, setMaterials]         = useState<any[]>([]);
  const [stats, setStats]                 = useState<any>(null);
  const [loading, setLoading]             = useState(true);
  const [search, setSearch]               = useState('');
  const [category, setCategory]           = useState('');
  const [showInactive, setShowInactive]   = useState(false);
  const [selected, setSelected]           = useState<any>(null);
  const [variants, setVariants]           = useState<any[]>([]);
  const [showAdd, setShowAdd]             = useState(false);
  const [editMaterial, setEditMaterial]   = useState<any>(null);
  const [addVariantFor, setAddVariantFor] = useState<any>(null);
  const [editVariant, setEditVariant]     = useState<any>(null);

  const load = async () => {
    setLoading(true);
    try {
      const [mRes, sRes] = await Promise.all([
        materialService.getAll({ category: category || undefined, search: search || undefined }),
        materialService.getStats(),
      ]);
      let data = mRes.data.data;
      if (!showInactive) data = data.filter((m: any) => m.is_active !== false);
      setMaterials(data);
      setStats(sRes.data.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [category, showInactive]);

  const openMaterial = async (mat: any) => {
    if (selected?.mat_id === mat.mat_id) { setSelected(null); return; }
    setSelected(mat);
    const r = await materialService.getVariants(mat.mat_id);
    setVariants(r.data.data);
  };

  const refreshVariants = async () => {
    if (selected) {
      const r = await materialService.getVariants(selected.mat_id);
      setVariants(r.data.data);
    }
  };

  const handleExport = () => {
    exportCSV(materials.map(m => ({
      Code: m.mat_code, Name: m.mat_name, Category: m.category,
      SubCategory: m.sub_category || '', Unit: m.unit_of_measure,
      Wastage: m.wastage_pct, Structural: m.is_structural ? 'Yes' : 'No',
      MinOrderQty: m.min_order_qty, Standard: m.is_standard || '',
      Active: m.is_active ? 'Yes' : 'No'
    })), 'materials.csv');
  };

  return (
    <>
      {showAdd && <MaterialModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
      {editMaterial && <MaterialModal material={editMaterial} onClose={() => setEditMaterial(null)}
        onSaved={() => { setEditMaterial(null); load(); }} />}
      {addVariantFor && <VariantModal matId={addVariantFor.mat_id} matName={addVariantFor.mat_name}
        onClose={() => setAddVariantFor(null)}
        onSaved={() => { setAddVariantFor(null); refreshVariants(); }} />}
      {editVariant && <VariantModal variant={editVariant} onClose={() => setEditVariant(null)}
        onSaved={() => { setEditVariant(null); refreshVariants(); }} />}

      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <p className="text-sm text-gray-500">
            {stats ? `${stats.total_materials} materials · ${stats.total_variants} variants` : ''}
          </p>
          <label className="flex items-center gap-1.5 cursor-pointer text-xs text-gray-500">
            <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)}
              className="w-3.5 h-3.5 rounded border-gray-300" />
            Show inactive
          </label>
        </div>
        <div className="flex gap-2">
          <button onClick={handleExport}
            className="flex items-center gap-2 px-3 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200">
            <Download size={14} /> Export
          </button>
          <button onClick={() => setShowAdd(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">
            <Plus size={15} /> Add Material
          </button>
        </div>
      </div>

      {/* Category chips */}
      {stats?.by_category && (
        <div className="flex flex-wrap gap-2 mb-4">
          <button onClick={() => setCategory('')}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors
              ${!category ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200'}`}>
            All ({stats.total_materials})
          </button>
          {Object.entries(stats.by_category).map(([cat, cnt]: any) => (
            <button key={cat} onClick={() => setCategory(cat === category ? '' : cat)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors
                ${category === cat ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200'}`}>
              {cat} ({cnt})
            </button>
          ))}
        </div>
      )}

      {/* Search */}
      <form onSubmit={e => { e.preventDefault(); load(); }} className="flex gap-2 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search materials..."
            className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <button type="submit" className="px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200">Search</button>
      </form>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Material</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Category</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Unit</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Wastage</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Structural</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Actions</th>
              <th className="w-8"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? [...Array(6)].map((_,i) => (
              <tr key={i}>{[...Array(7)].map((_,j) => (
                <td key={j} className="px-4 py-3"><div className="bg-gray-100 rounded h-4 animate-pulse" /></td>
              ))}<td /></tr>
            )) : materials.map(mat => (
              <React.Fragment key={mat.mat_id}>
                <tr onClick={() => openMaterial(mat)}
                  className={`hover:bg-blue-50 cursor-pointer transition-colors ${!mat.is_active ? 'opacity-50' : ''}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{mat.mat_name}</div>
                    <div className="text-xs text-gray-400">
                      {mat.mat_code}
                      {mat.sub_category && <span className="ml-1 text-gray-300">· {mat.sub_category}</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{mat.category}</td>
                  <td className="px-4 py-3 text-gray-600">{mat.unit_of_measure}
                    {mat.secondary_unit && <span className="text-xs text-gray-400 ml-1">/ {mat.secondary_unit}</span>}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{mat.wastage_pct}%</td>
                  <td className="px-4 py-3">
                    {mat.is_structural
                      ? <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Yes</span>
                      : <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">No</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${mat.is_active !== false ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {mat.is_active !== false ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                    <button onClick={() => setEditMaterial(mat)}
                      className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 bg-blue-50 px-2 py-1 rounded-lg">
                      <Edit2 size={11} /> Edit
                    </button>
                  </td>
                  <td className="px-4 py-3 text-gray-400">
                    {selected?.mat_id === mat.mat_id ? <ChevronDown size={16}/> : <ChevronRight size={16}/>}
                  </td>
                </tr>
                {selected?.mat_id === mat.mat_id && (
                  <tr className="bg-blue-50">
                    <td colSpan={8} className="px-4 py-3">
                      {/* Material extra details */}
                      <div className="flex gap-4 mb-3 text-xs text-gray-500">
                        {mat.min_order_qty && <span>Min Order: <strong>{mat.min_order_qty} {mat.unit_of_measure}</strong></span>}
                        {mat.is_standard && <span>Standard: <strong>{mat.is_standard}</strong></span>}
                        {mat.storage_notes && <span>Storage: <strong>{mat.storage_notes}</strong></span>}
                        {mat.substitutable && <span className="text-amber-600">⇄ Substitutable</span>}
                      </div>
                      {/* Variants */}
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-semibold text-gray-500">VARIANTS ({variants.length})</p>
                        <button onClick={() => setAddVariantFor(mat)}
                          className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800">
                          <Plus size={12} /> Add Variant
                        </button>
                      </div>
                      {variants.length === 0 ? (
                        <p className="text-xs text-gray-400">No variants yet.</p>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {variants.map((v: any) => (
                            <div key={v.variant_id} className="bg-white rounded-lg border border-gray-200 px-3 py-2 text-xs min-w-48">
                              <div className="flex items-center justify-between gap-3">
                                <div className="font-medium text-gray-900">{v.brand_name}</div>
                                <button onClick={() => setEditVariant({ ...v, mat_name: mat.mat_name })}
                                  className="text-blue-500 hover:text-blue-700">
                                  <Edit2 size={11} />
                                </button>
                              </div>
                              <div className="text-gray-500">{v.variant_name}</div>
                              <div className="flex items-center gap-2 mt-1 flex-wrap">
                                <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${TIER_COLORS[v.quality_tier]}`}>
                                  {v.quality_tier}
                                </span>
                                {v.applicable_bldg && v.applicable_bldg !== 'ALL' && (
                                  <span className="text-gray-400">{v.applicable_bldg}</span>
                                )}
                                <span className="text-gray-400">GST {v.gst_rate_pct}%</span>
                                {v.hsn_code && <span className="text-gray-400">HSN {v.hsn_code}</span>}
                              </div>
                              {/* Current rates inline */}
                              {v.current_rates && v.current_rates.length > 0 && (
                                <div className="mt-2 pt-2 border-t border-gray-100 space-y-1">
                                  {v.current_rates.map((r: any, i: number) => (
                                    <div key={i} className="flex justify-between text-gray-500">
                                      <span>{r.company_name}</span>
                                      <span className="font-medium text-gray-700">₹{Number(r.price_per_unit).toLocaleString()}/{r.unit_of_measure}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
        {!loading && materials.length === 0 && (
          <div className="text-center py-12 text-gray-400">No materials found.</div>
        )}
      </div>
    </>
  );
};

// ─────────────────────────────────────────────────────────────
// ── Suppliers Tab ─────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────
const SuppliersTab = () => {
  const [suppliers, setSuppliers]       = useState<any[]>([]);
  const [loading, setLoading]           = useState(true);
  const [showAdd, setShowAdd]           = useState(false);
  const [editSupplier, setEditSupplier] = useState<any>(null);
  const [approving, setApproving]       = useState<number | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await materialService.getSuppliers();
      setSuppliers(r.data.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const approve = async (supId: number) => {
    setApproving(supId);
    try {
      const { default: api } = await import('../../utils/api');
      await api.post(`/material/suppliers/${supId}/approve`);
      load();
    } finally { setApproving(null); }
  };

  const handleExport = () => {
    exportCSV(suppliers.map(s => ({
      Code: s.sup_code, Company: s.company_name, Contact: s.contact_person,
      Mobile: s.mobile, Email: s.email || '', City: s.city,
      Payment: s.payment_terms, LeadDays: s.lead_time_days,
      Approved: s.is_approved ? 'Yes' : 'No', Rating: s.rating || 0
    })), 'suppliers.csv');
  };

  // Supplier performance score
  const perfScore = (s: any) => {
    let score = 0;
    if (s.is_approved) score += 40;
    if (s.rating) score += s.rating * 8;
    if (s.lead_time_days <= 3) score += 20;
    else if (s.lead_time_days <= 7) score += 10;
    return Math.min(score, 100);
  };

  return (
    <>
      {showAdd && <SupplierModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
      {editSupplier && <SupplierModal supplier={editSupplier} onClose={() => setEditSupplier(null)}
        onSaved={() => { setEditSupplier(null); load(); }} />}

      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">{suppliers.length} suppliers</p>
        <div className="flex gap-2">
          <button onClick={handleExport}
            className="flex items-center gap-2 px-3 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200">
            <Download size={14} /> Export
          </button>
          <button onClick={() => setShowAdd(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">
            <Plus size={15} /> Add Supplier
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Supplier</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Contact</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">City</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Payment</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Rating</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Score</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? [...Array(4)].map((_,i) => (
              <tr key={i}>{[...Array(8)].map((_,j) => (
                <td key={j} className="px-4 py-3"><div className="bg-gray-100 rounded h-4 animate-pulse" /></td>
              ))}</tr>
            )) : suppliers.map(s => (
              <tr key={s.sup_id} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <div className="font-medium text-gray-900">{s.company_name}</div>
                  <div className="text-xs text-gray-400">{s.sup_code} · {s.gstin}</div>
                  {s.supply_areas && <div className="text-xs text-blue-500 mt-0.5">📍 {s.supply_areas}</div>}
                </td>
                <td className="px-4 py-3">
                  <div className="text-gray-700">{s.contact_person}</div>
                  <div className="text-xs text-gray-400">{s.mobile}</div>
                  {s.email && <div className="text-xs text-gray-400">{s.email}</div>}
                </td>
                <td className="px-4 py-3 text-gray-600">
                  {s.city}
                  {s.state && <div className="text-xs text-gray-400">{s.state}</div>}
                </td>
                <td className="px-4 py-3">
                  <div className="text-gray-600 text-xs">{s.payment_terms?.replace('_',' ')}</div>
                  <div className="text-xs text-gray-400">{s.lead_time_days}d lead</div>
                </td>
                <td className="px-4 py-3">
                  <StarRating value={s.rating || 0} readonly />
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1.5">
                    <div className="flex-1 bg-gray-200 rounded-full h-1.5 w-16">
                      <div className={`h-1.5 rounded-full ${perfScore(s) >= 70 ? 'bg-green-500' : perfScore(s) >= 40 ? 'bg-amber-400' : 'bg-red-400'}`}
                        style={{ width: `${perfScore(s)}%` }} />
                    </div>
                    <span className="text-xs text-gray-500">{perfScore(s)}</span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium
                    ${s.is_approved ? 'bg-green-100 text-green-700' : STATUS_COLORS[s.registration_status] || 'bg-gray-100 text-gray-500'}`}>
                    {s.is_approved ? 'APPROVED' : s.registration_status}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <button onClick={() => setEditSupplier(s)}
                      className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 bg-blue-50 px-2 py-1 rounded-lg">
                      <Edit2 size={11} /> Edit
                    </button>
                    {!s.is_approved && (
                      <button onClick={() => approve(s.sup_id)} disabled={approving === s.sup_id}
                        className="flex items-center gap-1 text-xs bg-green-50 text-green-700 border border-green-200 px-2 py-1 rounded-lg hover:bg-green-100 disabled:opacity-50">
                        <Check size={12} /> {approving === s.sup_id ? '...' : 'Approve'}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

// ─────────────────────────────────────────────────────────────
// ── Rates Tab ─────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────
const RatesTab = () => {
  const [materials, setMaterials] = useState<any[]>([]);
  const [selMat, setSelMat]       = useState('');
  const [rates, setRates]         = useState<any[]>([]);
  const [loading, setLoading]     = useState(false);
  const [showAdd, setShowAdd]     = useState(false);
  const [editRate, setEditRate]   = useState<any>(null);

  useEffect(() => {
    materialService.getAll({ category: '' }).then(r => setMaterials(r.data.data));
  }, []);

  const loadRates = async (matId: string) => {
    setSelMat(matId);
    if (!matId) { setRates([]); return; }
    setLoading(true);
    try {
      const r = await materialService.getL1Rates(Number(matId));
      setRates(r.data.data);
    } finally { setLoading(false); }
  };

  // Detect price alert: compare rank-1 rate vs previous
  const priceAlertPct = (r: any) => {
    if (!r.prev_price || !r.price_per_unit) return 0;
    return ((r.price_per_unit - r.prev_price) / r.prev_price) * 100;
  };

  // GST-inclusive price
  const gstInclusivePrice = (price: number, gstPct: number) =>
    (price * (1 + gstPct / 100)).toFixed(2);

  return (
    <>
      {showAdd && <RateModal onClose={() => setShowAdd(false)}
        onSaved={() => { setShowAdd(false); if (selMat) loadRates(selMat); }} />}
      {editRate && <RateModal rate={editRate} onClose={() => setEditRate(null)}
        onSaved={() => { setEditRate(null); if (selMat) loadRates(selMat); }} />}

      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">Supplier rates — select a material to view L1 (lowest) rates</p>
        <button onClick={() => setShowAdd(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">
          <Plus size={15} /> Add Rate
        </button>
      </div>

      <div className="mb-4">
        <label className="text-xs font-medium text-gray-600 block mb-1">Select Material</label>
        <select value={selMat} onChange={e => loadRates(e.target.value)}
          className="w-full max-w-sm border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="">Choose material...</option>
          {materials.map(m => <option key={m.mat_id} value={m.mat_id}>{m.mat_name}</option>)}
        </select>
      </div>

      {selMat && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Supplier</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Variant</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Tier</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Base Rate</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">GST Incl.</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Delivery</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Valid</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">L1</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-400">Loading rates...</td></tr>
              ) : rates.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-400">No rates found. Add supplier rates first.</td></tr>
              ) : rates.map((r: any) => {
                const alertPct = priceAlertPct(r);
                return (
                  <tr key={r.rate_id} className={`hover:bg-gray-50 ${r.rank === '1' ? 'bg-green-50' : ''}`}>
                    <td className="px-4 py-3 font-medium text-gray-900">{r.company_name}</td>
                    <td className="px-4 py-3 text-gray-600">{r.variant_name}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${TIER_COLORS[r.quality_tier]}`}>
                        {r.quality_tier}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <span className="font-semibold text-gray-900">₹{Number(r.price_per_unit).toLocaleString()}</span>
                        <span className="text-xs text-gray-400">/{r.unit_of_measure}</span>
                        <PriceAlertBadge pct={alertPct} />
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-xs">
                      ₹{Number(gstInclusivePrice(r.price_per_unit, r.gst_rate_pct || 18)).toLocaleString()}
                      <span className="text-gray-400 ml-1">(+{r.gst_rate_pct || 18}% GST)</span>
                    </td>
                    <td className="px-4 py-3">
                      {r.includes_delivery
                        ? <span className="text-xs text-green-600">Included</span>
                        : <span className="text-xs text-gray-400">
                            Extra{r.delivery_charges ? ` ₹${r.delivery_charges}` : ''}
                          </span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      <div>{fmtDate(r.effective_from)}</div>
                      {r.effective_to && <div className="text-gray-400">→ {fmtDate(r.effective_to)}</div>}
                    </td>
                    <td className="px-4 py-3">
                      {r.rank === '1'
                        ? <span className="flex items-center gap-1 text-xs text-green-700 font-semibold"><Star size={12} className="fill-green-500 text-green-500" /> L1</span>
                        : <span className="text-xs text-gray-400">#{r.rank}</span>}
                    </td>
                    <td className="px-4 py-3">
                      <button onClick={() => setEditRate(r)}
                        className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 bg-blue-50 px-2 py-1 rounded-lg">
                        <Edit2 size={11} /> Edit
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

// ─────────────────────────────────────────────────────────────
// ── Activity Mapping Tab ──────────────────────────────────────
// ─────────────────────────────────────────────────────────────
const ActivityMappingTab = () => {
  const [mappings, setMappings]     = useState<any[]>([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState('');
  const [filtered, setFiltered]     = useState<any[]>([]);
  const [catFilter, setCatFilter]   = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { default: api } = await import('../../utils/api');
        const r = await api.get('/material/activity-mappings');
        setMappings(r.data.data || []);
        setFiltered(r.data.data || []);
      } catch {
        setMappings([]); setFiltered([]);
      } finally { setLoading(false); }
    };
    load();
  }, []);

  useEffect(() => {
    let data = mappings;
    if (catFilter) data = data.filter((m: any) => m.category === catFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      data = data.filter((m: any) =>
        m.activity_name?.toLowerCase().includes(q) ||
        m.mat_name?.toLowerCase().includes(q) ||
        m.act_code?.toLowerCase().includes(q)
      );
    }
    setFiltered(data);
  }, [search, catFilter, mappings]);

  // Group by activity
  const grouped: Record<string, any> = filtered.reduce((acc: any, row: any) => {
    const key = row.act_code;
    if (!acc[key]) acc[key] = { act_code: row.act_code, activity_name: row.activity_name, materials: [] };
    acc[key].materials.push(row);
    return acc;
  }, {});

  // Unique categories in mappings
  const categories = mappings.map((m: any) => m.category).filter((c: string, i: number, arr: string[]) => arr.indexOf(c) === i).sort();

  const handleExport = () => {
    exportCSV(mappings.map(m => ({
      ActivityCode: m.act_code, ActivityName: m.activity_name,
      MaterialCode: m.mat_code, MaterialName: m.mat_name,
      Category: m.category, Unit: m.unit_of_measure,
      QtyPerUnit: m.qty_per_unit || '', QualityTier: m.quality_tier || '',
      Mandatory: m.is_mandatory ? 'Yes' : 'No'
    })), 'activity_material_map.csv');
  };

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">
          {mappings.length} mappings · {Object.keys(grouped).length} activities shown
        </p>
        <div className="flex gap-2">
          <button onClick={handleExport}
            className="flex items-center gap-2 px-3 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200">
            <Download size={14} /> Export
          </button>
        </div>
      </div>

      <div className="flex gap-3 mb-4 flex-wrap">
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search activity or material..."
            className="pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-64" />
        </div>
        <select value={catFilter} onChange={e => setCatFilter(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="">All Categories</option>
          {categories.map(c => <option key={c as string}>{c as string}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="space-y-2">
          {[...Array(5)].map((_,i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="bg-gray-100 rounded h-4 w-48 animate-pulse mb-2" />
              <div className="bg-gray-100 rounded h-3 w-32 animate-pulse" />
            </div>
          ))}
        </div>
      ) : Object.keys(grouped).length === 0 ? (
        <div className="text-center py-12 text-gray-400">No mappings found.</div>
      ) : (
        <div className="space-y-2">
          {Object.values(grouped).map((grp: any) => (
            <div key={grp.act_code} className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <span className="text-xs font-mono bg-gray-100 text-gray-600 px-2 py-0.5 rounded mr-2">
                    {grp.act_code}
                  </span>
                  <span className="text-sm font-medium text-gray-900">{grp.activity_name}</span>
                </div>
                <span className="text-xs text-gray-400">
                  {grp.materials.length} material{grp.materials.length > 1 ? 's' : ''}
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {grp.materials.map((m: any, i: number) => (
                  <div key={i} className={`flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs border
                    ${m.is_mandatory ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}>
                    <span className="font-medium text-gray-800">{m.mat_name}</span>
                    {m.qty_per_unit && (
                      <span className="text-gray-500">{m.qty_per_unit} {m.unit_of_measure}</span>
                    )}
                    {m.quality_tier && (
                      <span className={`px-1.5 py-0.5 rounded font-medium ${TIER_COLORS[m.quality_tier] || 'bg-gray-100 text-gray-600'}`}>
                        {m.quality_tier}
                      </span>
                    )}
                    {m.is_mandatory && (
                      <span className="text-red-600 font-semibold">*</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

// ─────────────────────────────────────────────────────────────
// ── Main MaterialsPage ────────────────────────────────────────
// ─────────────────────────────────────────────────────────────
const MaterialsPage = () => {
  const [tab, setTab] = useState<'materials'|'suppliers'|'rates'|'mapping'>('materials');

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-900">Material Master</h1>
        <p className="text-sm text-gray-500 mt-0.5">Materials, variants, suppliers, rates and activity mappings</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit mb-6">
        {([
          { key: 'materials', label: 'Materials',        icon: <Package size={14}/>   },
          { key: 'suppliers', label: 'Suppliers',        icon: <Building2 size={14}/> },
          { key: 'rates',     label: 'Rates',            icon: <TrendingUp size={14}/>},
          { key: 'mapping',   label: 'Activity Mapping', icon: <Activity size={14}/>  },
        ] as const).map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 px-4 py-1.5 text-sm font-medium rounded-md transition-colors
              ${tab === t.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            {t.icon}{t.label}
          </button>
        ))}
      </div>

      {tab === 'materials' && <MaterialsTab />}
      {tab === 'suppliers' && <SuppliersTab />}
      {tab === 'rates'     && <RatesTab />}
      {tab === 'mapping'   && <ActivityMappingTab />}
    </div>
  );
};

export default MaterialsPage;
